import React from 'react';
import { connect } from '@gasbuddy/react';
import { Loader } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';


import MainLayout from '../../components/main-layout';
import Card from '../../components/card';
import Notification from '../../components/notification';
import NotFound from '../../components/not-found';
import styles from './styles.css';
import JobModal from './job-modal';
import JobList from './job-list';
import Button from '../../components/button';
import RoleAccess from '../../components/role-access';

import { Roles } from '../../../lib/constants/enums';

const { api } = require('../../../lib/bputils').default;

const rolesCanAccess = [Roles.Administrator, Roles.Editor, Roles.Observer];

function AlertManagement() {
  // b/c the MainLayout is required to have children components (markup inside the component tag)
  // we put this <div> does nothing, it will be removed at a later time when we have only components to put inside of it
  const [isLoading, setIsLoading] = React.useState(true);
  const [ratingCategories, setRatingCategories] = React.useState(null);
  const [stationLists, setStationLists] = React.useState(null);
  const [stations, setStations] = React.useState(null);
  const [brands, setBrands] = React.useState(null);
  const [organizationSolutions, setOrganizationSolutions] = React.useState(null);
  const [user, setUser] = React.useState(null);
  const [canAccessPage, setCanAccessPage] = React.useState(false);


  const [jobs, setJobs] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);
  const [activeJob, setActiveJob] = React.useState(null);

  React.useEffect(() => {
    getUser();
  }, []);

  React.useEffect(() => {
    if (user && canAccessPage) {
      getUserJobs();
      getRatingCategories();
      getStationLists();
      getStations();
      getBrands();
      getOrganization();
    }
  }, [user, canAccessPage]);

  const handleOnModalClose = () => {
    setShowModal(false);
    setActiveJob(null);
  };

  const handleOnJobEdit = (job) => {
    setActiveJob(job);
    setShowModal(true);
  };

  const getRatingCategories = async () => api.get({
    requestId: 'getAllRatingCategories',
    path: '/rating-categories',
    success: (res) => {
      setRatingCategories(res || []);
    },
    error: () => {
      Notification.error('Unable to fetch rating categories');
    },
  });

  const getStationLists = async () => api.get({
    requestId: 'getAllStationLists',
    path: '/station-lists',
    success: (res) => {
      setStationLists(res);
    },
    error: () => {
      Notification.error('Unable to fetch your station lists');
    },
  });

  const getStations = async () => api.get({
    requestId: 'getAllStations',
    path: '/stations?response_type=stations_without_address',
    success: (res) => {
      setStations(res);
    },
    error: () => {
      Notification.error('Unable to fetch your stations');
    },
  });

  const getBrands = async () => api.get({
    requestId: 'getAllBrands',
    path: '/brands',
    success: (res) => {
      setBrands(res);
    },
    error: () => {
      Notification.error('Unable to fetch brands');
    },
  });

  const getOrganization = async () => api.get({
    requestId: 'getOrganization',
    path: '/organization',
    success: (res) => {
      setOrganizationSolutions(res.solutions.map(s => s.name));
    },
    error: () => {
      Notification.error('Unable to fetch organization');
    },
  });

  const getUser = async () => api.get({
    requestId: 'getUser',
    path: '/users/me',
    success: (res) => {
      setUser(res);
    },
    error: () => {
      Notification.error('Unable to fetch user info');
    },
  });

  React.useEffect(() => {
    if (jobs && ratingCategories && stationLists && stations && brands && organizationSolutions && user) {
      setIsLoading(false);
    }
  }, [jobs, ratingCategories, stationLists, stations, brands, organizationSolutions, user]);

  const getUserJobs = async () => api.get({
    requestId: 'getAllJobs',
    path: '/jobs',
    success: (res) => {
      setJobs(res?.user_jobs || []);
    },
    error: () => {
      Notification.error('Unable to fetch your alerts');
    },
  });

  const handleOnAdd = (newJob) => {
    const jobToAdd = { ...newJob };
    jobToAdd.jobType = { name: newJob.jobType };
    jobToAdd.jobSchedule = { name: newJob.jobSchedule };
    setJobs([...jobs, jobToAdd]);
  };

  const handleOnDelete = (id) => {
    const remainingJobs = jobs.filter(j => j.id !== id);
    setJobs(remainingJobs);
  };

  const handleOnEdit = (id, job) => {
    const jobIndex = jobs.findIndex(j => j.id === id);
    const editableJobProps = {
      parameters: job.job_options,
      enabled: job.enabled,
      display_name: job.display_name,
    };
    const editedJob = { ...jobs[jobIndex], ...editableJobProps };
    const newJobs = [...jobs];
    newJobs[jobIndex] = editedJob;
    setJobs(newJobs);
  };

  return (
    <MainLayout title="Scheduled Reports Management">
      <RoleAccess fetching={isLoading} roles={rolesCanAccess} userRole={user?.roles[0] || null} callback={setCanAccessPage}>
        {
          isLoading ? <Loader className={styles.loader} /> : (
            <>
              <div className={styles.primaryButtonsContainer}>
                <Button primary onClick={() => setShowModal(true)} size="sm">
                  <FontAwesomeIcon icon={faPlus} />
                  &nbsp;&nbsp;
                  <span>Create a Report</span>
                </Button>
              </div>
              <Card title={`Current Reports (${jobs.length})`}>
                { jobs.length ? (
                  <JobList
                    jobs={jobs}
                    ratingCategories={ratingCategories}
                    stationLists={stationLists}
                    stations={stations}
                    brands={brands}
                    onEdit={handleOnJobEdit}
                  />
                ) : (
                  <NotFound
                    message={'Sorry, no scheduled reports were found. Try adding a report by clicking on the "+ Create a Report" button.'}
                  />
                )}
              </Card>
            </>
          )
        }
      </RoleAccess>
      <JobModal
        showModal={showModal}
        onClose={handleOnModalClose}
        onAdd={handleOnAdd}
        onDelete={handleOnDelete}
        onEdit={handleOnEdit}
        ratingCategories={ratingCategories}
        stationLists={stationLists}
        stations={stations}
        job={activeJob}
        brands={brands}
        organizationSolutions={organizationSolutions}
        email={user?.email || ''}
      />
    </MainLayout>
  );
}

export default connect()(AlertManagement);
